import { useMemo } from 'react';

import {
  Pathable,
  PathsSelectorFrom,
  pathsSelector as realizePaths,
  isSuperPath,
  FormLens,
} from '@tdp/common/form';

/**
 * Creates a convenient consolidation of only the validation errors for a set of sub-parts of the model/form.
 *
 * This is exceptionally experimental and only supports validations right now.
 *
 * @param modelForm The model form
 * @param pathsSelector the selector into the sub-model/form
 * @returns a tuple of [isInvalid, validationErrors]
 */
export function useFormValidations<TSchema extends Pathable>(
  modelForm: FormLens<TSchema>,
  pathsSelector: PathsSelectorFrom<TSchema>
) {
  /*
   * This is likely not an optimal or generalizable solution for traversal(s)
   * read: https://hackage.haskell.org/package/optics-core-0.1/docs/Optics-Traversal.html
   * read: https://www.schoolofhaskell.com/school/to-infinity-and-beyond/pick-of-the-week/a-little-lens-starter-tutorial#i-think-i-understand-prisms-now--what-are-traversals-
   * As of right now, I don't see a clean and readably way to define traversals in TS as you can observe in the example here:
   *  https://chrispenner.ca/posts/traversal-systems#keeping-references which shows how ugly this is without a DSL. Typescript has no first-class monad or way of declaring DSLs
   * so I think we sidestep the issue by just writing bespoke traversals if/when needed such as this one.
   */
  const paths = useMemo(() => realizePaths(pathsSelector), [pathsSelector]);
  // This is probably wildly inefficient for large forms with many validation errors. (NxMxL complexity for N errors, M paths and L path lengths)
  const validationErrors = useMemo(
    () =>
      modelForm.validationErrors.filter((error) =>
        paths.some((path) => isSuperPath(path, error.path))
      ),
    [modelForm.validationErrors, paths]
  );

  return [validationErrors.length > 0, validationErrors] as const; //const cast so typescript knows it is a tuple.
}
